import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, CalendarClock } from 'lucide-react';
import { DatePicker } from 'antd';
const { RangePicker } = DatePicker;
import dayjs from 'dayjs';
import { cn } from '@/lib/utils';
import { AnalyticsFilters, AnalyticsMode } from './types/analytics';
import { getDefaultDateRange, getDefaultAggregation, getAvailableAggregations, POWER_METRICS, TEMPERATURE_METRICS } from './utils/data-processing';
import { TimeAggregationControls } from './components/TimeAggregationControls';
import { AnalyticsChart } from './components/AnalyticsChart';
import { StatisticsSummary } from './components/StatisticsSummary';
import { useAnalyticsData, useComparisonAnalyticsData } from './hooks/useAnalyticsData';
import { useMetricsCalculation, useComparisonMetricsCalculation } from './hooks/useMetricsCalculation';

const ANALYTICS_MODES: AnalyticsMode[] = [
  {
    key: 'power-temp',
    label: 'Power & Temperature Trends',
    description: 'Real-time power consumption and temperature monitoring with rolling averages'
  },
  {
    key: 'energy',
    label: 'Energy Consumption Analysis', 
    description: 'Energy usage patterns and efficiency metrics with aggregated data'
  }
];

export default function AnalyticsPage() {
  const defaultDateRange = getDefaultDateRange();
  
  // Get all available metrics
  const getAllMetrics = (mode: 'power-temp' | 'energy') => {
    const powerMetrics = POWER_METRICS.map(m => m.key);
    const tempMetrics = TEMPERATURE_METRICS.map(m => m.key);
    return [...powerMetrics, ...tempMetrics];
  };

  const [filters, setFilters] = useState<AnalyticsFilters>({
    mode: 'power-temp',
    selectedMetrics: getAllMetrics('power-temp'), // Show all metrics by default
    dateRange: defaultDateRange,
    timeAggregation: getDefaultAggregation('power-temp', defaultDateRange.start, defaultDateRange.end),
    comparison: {
      enabled: false,
      range1: defaultDateRange
    }
  });


  const handleModeChange = (mode: AnalyticsMode['key']) => {
    if (mode === filters.mode) return; // No change needed
    
    const newAggregation = getDefaultAggregation(mode, filters.dateRange.start, filters.dateRange.end);
    
    setFilters(prev => ({
      ...prev,
      mode,
      timeAggregation: newAggregation,
      selectedMetrics: getAllMetrics(mode), // Show all metrics for the new mode
      // Force refresh to trigger new API calls
      __forceRefreshRange1: Date.now()
    }));
  };

  const [tempDateRange, setTempDateRange] = useState([
    dayjs(filters.dateRange.start),
    dayjs(filters.dateRange.end)
  ]);

  const [tempComparisonRange, setTempComparisonRange] = useState([
    filters.comparison.range2?.start ? dayjs(filters.comparison.range2.start) : null,
    filters.comparison.range2?.end ? dayjs(filters.comparison.range2.end) : null
  ]);

  const handleDateRangeChange = (start: string, end: string) => {
    const newAggregation = getDefaultAggregation(filters.mode, start, end);
    
    setFilters(prev => ({
      ...prev,
      dateRange: { start, end },
      timeAggregation: newAggregation,
      comparison: {
        ...prev.comparison,
        range1: { start, end }
      }
    }));
  };

  const handleApplyRange1 = () => {
    if (!tempDateRange[0] || !tempDateRange[1]) return;
    
    const startDate = tempDateRange[0].format('YYYY-MM-DD');
    const endDate = tempDateRange[1].format('YYYY-MM-DD');
    
    const datesChanged = startDate !== filters.dateRange.start || 
                        endDate !== filters.dateRange.end;
    
    if (datesChanged) {
      handleDateRangeChange(startDate, endDate);
    }
    
    // Always force refresh to ensure data updates
    setFilters(prev => ({ ...prev, __forceRefreshRange1: Date.now() }));
  };

  const handleApplyRange2 = () => {
    if (!tempComparisonRange[0] || !tempComparisonRange[1]) return;
    
    const startDate = tempComparisonRange[0].format('YYYY-MM-DD');
    const endDate = tempComparisonRange[1].format('YYYY-MM-DD');
    
    setFilters(prev => ({
      ...prev,
      comparison: {
        ...prev.comparison,
        range2: {
          start: startDate,
          end: endDate
        }
      },
      // Always force refresh to ensure data updates
      __forceRefreshRange2: Date.now()
    }));
  };

  // Remove metric toggle function since we're showing all metrics
  // const handleMetricToggle = (metricKey: string) => { ... }

  const handleAggregationChange = (aggregation: string) => {
    setFilters(prev => ({
      ...prev,
      timeAggregation: aggregation
    }));
  };

  const handleComparisonToggle = () => {
    setFilters(prev => {
      const isEnabling = !prev.comparison.enabled;
      // Calculate Range 2 as consecutive period before Range 1
      const range1Start = new Date(prev.dateRange.start);
      const range1End = new Date(prev.dateRange.end);
      const duration = range1End.getTime() - range1Start.getTime(); // Duration in milliseconds
      
      const newRange2 = isEnabling ? {
        end: new Date(range1Start.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Range 1 start - 1 day
        start: new Date(range1Start.getTime() - duration - 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Range 1 start - 1 day - duration
      } : undefined;

      // Update temp comparison range when enabling
      if (isEnabling && newRange2) {
        setTempComparisonRange([
          dayjs(newRange2.start),
          dayjs(newRange2.end)
        ]);
      }

      return {
        ...prev,
        comparison: {
          ...prev.comparison,
          enabled: isEnabling,
          range2: newRange2
        }
      };
    });
  };

  // Data fetching - Always fetch both to avoid data loss on mode switching
  const { data, isLoading, error } = useAnalyticsData({ 
    filters,
    enabled: true // Always enabled since we show all metrics
  });

  const { 
    range1Data, 
    range2Data, 
    isLoading: isComparisonLoading,
    range1Loading,
    range2Loading,
    range1Error,
    range2Error
  } = useComparisonAnalyticsData({ 
    filters,
    enabled: filters.comparison.enabled // Always enabled when comparison is on
  });

  // Data processing - Use appropriate data source based on mode
  const { chartData, statistics } = useMetricsCalculation({ 
    data: filters.comparison.enabled ? range1Data : data, 
    filters 
  });

  const { 
    range1ChartData, 
    range2ChartData, 
    range1Statistics, 
    range2Statistics 
  } = useComparisonMetricsCalculation({
    range1Data,
    range2Data,
    filters
  });

  // Debug Range 2 data
  useEffect(() => {
    if (filters.comparison.enabled && filters.comparison.range2) {
      console.log('Range 2 Debug:', {
        range2Data: range2Data?.length || 0,
        range2ChartData: range2ChartData?.timestamps?.length || 0,
        range2Loading,
        range2Error,
        range2Filter: filters.comparison.range2,
        forceRefresh: filters.__forceRefreshRange2
      });
    }
  }, [filters.comparison.enabled, filters.comparison.range2, range2Data, range2ChartData, range2Loading, range2Error, filters.__forceRefreshRange2]);

  // Use appropriate loading and error states based on mode
  const range1CurrentLoading = filters.comparison.enabled ? range1Loading : isLoading;
  const range1CurrentError = filters.comparison.enabled ? range1Error : error;
  const range2CurrentLoading = range2Loading;
  const range2CurrentError = range2Error;
  
  // Ensure we have data for single mode when switching back from comparison
  const effectiveChartData = filters.comparison.enabled ? range1ChartData : chartData;
  const effectiveStatistics = filters.comparison.enabled ? range1Statistics : statistics;
  

  // Calculate combined Y-axis ranges for comparison mode
  const calculateYAxisRange = (chartData1: any, chartData2: any) => {
    if (!filters.comparison.enabled || !chartData1?.series || !chartData2?.series) {
      return undefined;
    }

    const primarySeries1 = chartData1.series.filter((s: any) => s.yAxisIndex === 0);
    const primarySeries2 = chartData2.series.filter((s: any) => s.yAxisIndex === 0);
    const secondarySeries1 = chartData1.series.filter((s: any) => s.yAxisIndex === 1);
    const secondarySeries2 = chartData2.series.filter((s: any) => s.yAxisIndex === 1);

    let primaryRange = undefined;
    let secondaryRange = undefined;

    // Calculate primary axis range (power/energy)
    if (primarySeries1.length > 0 || primarySeries2.length > 0) {
      const allPrimaryValues = [
        ...primarySeries1.flatMap((s: any) => s.data),
        ...primarySeries2.flatMap((s: any) => s.data)
      ].filter((v: number) => v !== null && v !== undefined && !isNaN(v));

      if (allPrimaryValues.length > 0) {
        const min = Math.min(...allPrimaryValues);
        const max = Math.max(...allPrimaryValues);
        const padding = (max - min) * 0.1; // 10% padding
        primaryRange = {
          min: Math.max(0, min - padding),
          max: max + padding
        };
      }
    }

    // Fixed secondary axis range (temperature) 15-50°C
    secondaryRange = {
      min: 15,
      max: 50
    };

    return {
      primary: primaryRange,
      secondary: secondaryRange
    };
  };

  const yAxisRange = calculateYAxisRange(range1ChartData, range2ChartData);


  // Sync temp date ranges with actual filters
  useEffect(() => {
    setTempDateRange([
      dayjs(filters.dateRange.start),
      dayjs(filters.dateRange.end)
    ]);
  }, [filters.dateRange]);

  useEffect(() => {
    if (filters.comparison.range2) {
      setTempComparisonRange([
        dayjs(filters.comparison.range2.start),
        dayjs(filters.comparison.range2.end)
      ]);
    }
  }, [filters.comparison.range2]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="min-h-full bg-bg p-3 flex flex-col">
      {/* Header Controls */}
      <div className="bg-white rounded-lg shadow-card border border-light-grey p-3 mb-3">
        {/* First Row: Mode on left, Controls on right */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-3">
          {/* Left: Mode Toggle */}
          <div className="flex items-center gap-2">
            <div className="flex bg-bg rounded-lg p-1 gap-1">
              {ANALYTICS_MODES.map((mode, index) => (
                <button
                  key={mode.key}
                  onClick={() => handleModeChange(mode.key)}
                  className={cn(
                    "px-3 py-1.5 paragraph-12 font-medium transition-all rounded-md",
                    filters.mode === mode.key
                      ? "bg-white text-black shadow-sm"
                      : "text-dark-grey hover:text-black hover:bg-white/50"
                  )}
                >
                  <span className="hidden sm:inline">{mode.label}</span>
                  <span className="sm:hidden">{mode.key === 'power-temp' ? 'Power' : 'Energy'}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Time Aggregation + Metric Selectors */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Time Aggregation - Compact */}
            <div className="flex items-center gap-2">
              <span className="paragraph-12 text-dark-grey">
                {filters.mode === 'power-temp' ? 'Rolling:' : 'Period:'}
              </span>
              <div className="flex bg-bg rounded-lg p-1 gap-1">
                {getAvailableAggregations(filters.mode, filters.dateRange.start, filters.dateRange.end).map((agg) => (
                  <button
                    key={agg.key}
                    onClick={() => handleAggregationChange(agg.key)}
                    className={cn(
                      "px-2.5 py-1.5 paragraph-12 font-medium transition-all rounded-md",
                      filters.timeAggregation === agg.key
                        ? "bg-white text-black shadow-sm"
                        : "text-dark-grey hover:text-black hover:bg-white/50"
                    )}
                  >
                    {agg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* All metrics shown by default - no selector needed */}
          </div>
        </div>

        {/* Second Row: Date Range Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 pt-3 border-t border-light-grey">
          {/* Left: Range 1 */}
          <div className="flex items-center gap-2">
            <span className="paragraph-12 font-medium text-dark-grey">Range 1:</span>
            <RangePicker
              value={tempDateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setTempDateRange([dates[0], dates[1]]);
                }
              }}
              allowClear={false}
              format="YYYY-MM-DD"
              size="small"
              className="border border-light-grey hover:border-primary focus:border-primary rounded-md text-xs"
              style={{ height: '28px' }}
              suffixIcon={<CalendarClock className="h-3 w-3 text-primary" />}
            />
            <button 
              onClick={handleApplyRange1}
              className="px-3 py-1.5 paragraph-12 text-primary border border-primary rounded-md hover:bg-primary hover:text-white transition-all duration-200"
            >
              Apply
            </button>
          </div>

          {/* Right: Range 2 and Compare Toggle */}
          <div className="flex items-center gap-3">
            {/* Range 2 (when comparison enabled) */}
            {filters.comparison.enabled && filters.comparison.range2 && (
              <div className="flex items-center gap-2">
                <span className="paragraph-12 font-medium text-dark-grey">Range 2:</span>
                <RangePicker
                  value={tempComparisonRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setTempComparisonRange([dates[0], dates[1]]);
                    }
                  }}
                  allowClear={false}
                  format="YYYY-MM-DD"
                  size="small"
                  className="border border-light-grey hover:border-primary focus:border-primary rounded-md text-xs"
                  style={{ height: '28px' }}
                  suffixIcon={<CalendarClock className="h-3 w-3 text-primary" />}
                />
                <button 
                  onClick={handleApplyRange2}
                  className="px-3 py-1.5 paragraph-12 text-primary border border-primary rounded-md hover:bg-primary hover:text-white transition-all duration-200"
                >
                  Apply
                </button>
              </div>
            )}

            {/* Compare Toggle */}
            <button
              onClick={handleComparisonToggle}
              className={cn(
                "px-3 py-1.5 paragraph-12 rounded-md transition-all duration-200 border flex-shrink-0",
                filters.comparison.enabled
                  ? "bg-primary text-white border-primary"
                  : "text-dark-grey border-light-grey hover:border-primary hover:text-primary"
              )}
            >
              Compare
            </button>
          </div>
        </div>
      </div>

      {/* Main Chart Area */}
      <div className="mb-3">
        {filters.comparison.enabled ? (
          /* Comparison Mode - Two Charts Side by Side */
          <div className="bg-white rounded-lg border border-light-grey min-h-[500px]">
            <div className="flex h-[500px]">
              {/* Left Chart */}
              <div className="flex-1 px-2">
                <AnalyticsChart
                  data={range1ChartData}
                  mode={filters.mode}
                  timeAggregation={filters.timeAggregation}
                  isLoading={range1CurrentLoading}
                  error={range1CurrentError}
                  title={undefined}
                  height={500}
                  yAxisRange={yAxisRange}
                />
              </div>
              
              {/* Separator Line */}
              <div className="w-px bg-light-grey"></div>
              
              {/* Right Chart - Only show when Range 2 is configured */}
              {filters.comparison.range2 ? (
                <div className="flex-1 px-2">
                  <AnalyticsChart
                    data={range2ChartData}
                    mode={filters.mode}
                    timeAggregation={filters.timeAggregation}
                    isLoading={range2CurrentLoading}
                    error={range2CurrentError}
                    title={undefined}
                    height={500}
                    yAxisRange={yAxisRange}
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <div className="text-sm">Select Range 2 dates and click Apply to compare</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Single Chart Mode */
          <div className="bg-white rounded-lg border border-light-grey min-h-[500px]">
            <div className="h-[500px] px-2">
              <AnalyticsChart
                data={effectiveChartData}
                mode={filters.mode}
                timeAggregation={filters.timeAggregation}
                isLoading={range1CurrentLoading}
                error={range1CurrentError}
                title={undefined}
                height={500}
              />
            </div>
          </div>
        )}
      </div>

      {/* Statistics Summary */}
      <div className="mb-6">
        <StatisticsSummary
        statistics={effectiveStatistics}
        comparisonStatistics={filters.comparison.enabled ? range2Statistics : undefined}
        isComparison={filters.comparison.enabled}
        range1Label={undefined}
        range2Label={undefined}
        isLoading={filters.comparison.enabled ? (range1CurrentLoading || range2CurrentLoading) : range1CurrentLoading}
        mode={filters.mode}
        />
      </div>
      </div>
    </div>
  );
}